import * as React from 'react';
import { useAppSetting } from '@contexts/appSetting/hooks/useAppSetting';
import { PageHeader, PageHeaderProps } from '@v2/components/Header/PageHeader';
import { useServiceResolver } from '@services/useServiceResolver';
import { Icon } from '@drone-js/core';

export type NavHeaderContext = {
    setNavHeader: (props: PageHeaderProps) => void;
    setShowNavHeader: (show: boolean) => void;
    clearNavHeader: () => void;
};

export const NavHeaderContext = React.createContext<NavHeaderContext>({
    setNavHeader: () => {},
    setShowNavHeader: () => {},
    clearNavHeader: () => {},
});

export function withNavHeader<TProps>(Component: React.FC<TProps>): React.FC<React.PropsWithRef<TProps>> {
    const MemoizedComponent = React.memo<React.FC<TProps>>(Component);

    const ComponentWithNavHeader: React.FC<React.PropsWithRef<TProps>> = props => {
        const { customUI } = useAppSetting();
        const { sdkEffect } = useServiceResolver();

        // Default is exit
        const defaultNavHeader = {
            header: 'Header Title',
            onButtonClick: sdkEffect.onExit,
            buttonIcon: <Icon id="fill.arrow.thin-back" size="14" />,
        };

        const [showNavHeader, setShowNavHeader] = React.useState<boolean>(false);
        const [navHeader, setNavHeader] = React.useState<PageHeaderProps>(defaultNavHeader);
        const clearNavHeader = React.useCallback(() => setNavHeader(defaultNavHeader), []);

        return (
            <NavHeaderContext.Provider value={{ setNavHeader, clearNavHeader, setShowNavHeader }}>
                {showNavHeader && <PageHeader {...navHeader} />}
                <MemoizedComponent {...props} />
            </NavHeaderContext.Provider>
        );
    };

    ComponentWithNavHeader.displayName = `${Component.displayName || 'Component'}WithNavHeader`;

    return ComponentWithNavHeader;
}
